/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import loadEnvironmentConfig from '@ion/applications/src/env/env';
import {
  Config,
  Environment,
  Environments
} from '@ion/core/src/environment/environment';

/**
 * Maps each deployment environment to its corresponding Admin domain URL from
 * which the environment configuration should be loaded.
 *
 * ```typescript
 *  // load EU environment.json in QA ...
 *  https://qa.admin.calamp.com/login?environment=eu
 *
 *  // load US environment.json in QA ...
 *  https://qa.admin.calamp.com/login?environment=us
 *
 *  // load QA environment.json in DEV ...
 *  https://dev.admin.calamp.com/login?environment=qa
 *
 *  // load DEV environment.json in QA ...
 *  https://qa.admin.calamp.com/login?environment=dev
 *
 *  // load STG environment.json in QA ...
 *  https://qa.admin.calamp.com/login?environment=stg
 * ```
 *
 * @see {@link loadEnvironmentConfig}
 * @see {@link Environments}
 */
const domains: Partial<Environments> = {
  [Environment.dev] : 'https://dev.admin.calamp.com',
  [Environment.qa]  : 'https://qa.admin.calamp.com',
  [Environment.stg] : 'https://stg.admin.calamp.com',
  [Environment.eu]  : 'https://admin.calamp.co.uk',
  [Environment.us]  : 'https://admin.calamp.com'
};

/**
 * Loads the environment configuration for the ADM application, defaulting to loading
 * the `environment.json` document from the host domain. If a valid `environment` query
 * parameter is provided which maps to the predefined domain mappings for other Admin
 * environments, the `environment.json` document for the given remote domain will be
 * loaded instead. If the remote environment JSON fails to load, this function will
 * fallback to loading the root domain `environment.json` document.
 *
 * @see     {@link Config}
 * @see     {@link Environment}
 * @see     {@link Environments}
 * @see     {@link getEnvironmentDetails}
 * @returns A promise that resolves with the parsed JSON content (configuration) of
 *          the `environment.json` document from the specified environment.
 */
const loadConfig = async(): Promise<Config> => (
  await loadEnvironmentConfig(domains) as unknown as Config
);
export default loadConfig;
